import { BackdropCircularProgress, EmailFieldRHF, EnhancedButtonPrimeReact, EnhancedButtonPrimeReactSeverity, EnhancedDropDownRHF, EnhancedDropDownRHFWithLabel, EnhancedSnackbar, LabelPosition, RHookFormTextField, RHookFormTextFieldWithLabel } from "@websential/cosmic-v20";
import { Box, Grid, useTheme } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { IAppState } from "../../../store/reducers";
import { IDropDownListResponse } from "../../../models/common/IDropDownListResponse";
import { ReportsService } from "../../../services/ReportsService";
import { TransactionDetailDataTable } from "./TransactionDetailDataTable";
import { setSelectedActivityLogActions } from "../../../store/actions/activityLogActions";
import { setSelectedDrawerOptionsMenu } from "../../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../../store/actions/stickyNavBarActions";
import { sortDDLByLabel } from "../../../common/Common";
import FileSaver from "file-saver";
import { INITIAL_SNACKBAR_DATA } from "../../../common/constants";
import { IGetListRequest } from "../../../models";

export interface IInvoiceTransactionDetail { }

const useTransactionDetails = (
  clientId: string,
  taxYear: number,
  invoiceNo: string,
) =>
  useQuery({
    queryKey: ["Transaction-detail", clientId, taxYear, invoiceNo],
    queryFn: (data) => {
      return ReportsService.getInvoiceTransactionDetail(clientId, taxYear, invoiceNo);

    },
    refetchOnWindowFocus: false
  });

const useGetInvoiceNumbersDDL = (clientId: string, taxYear: number) =>
  useQuery({
    queryKey: ["invoice-numbers-ddl"],
    queryFn: (data) => {
      return ReportsService.getInvoiceNumbersDDL(clientId, taxYear);
    },
    refetchOnWindowFocus: false,
  });
export const InvoiceTransactionDetail: React.FC<IInvoiceTransactionDetail> = () => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  // const navigate = useNavigate();
  const dispatch = useDispatch();
  const clientId = useSelector((state: IAppState) => state.client.id);
  const clientName = useSelector((state: IAppState) => state.client.clientName);
  const taxYear = useSelector((state: IAppState) => state.taxYear.year);
  const storedInvoiceNoId = useSelector((state: IAppState) => state.invoice.id);
  const [invoiceNo, setInvoiceNo] = useState<any>(storedInvoiceNoId)
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);


  const formMethods = useForm<any>({
    // resolver: yupResolver(ValidationSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
  });


  const onSnackbarClosed = (
    event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarData(INITIAL_SNACKBAR_DATA);
  };


  const { isPending: isPendingInvoiceNumbersDDL, data: invoiceNumbersDDL } = useGetInvoiceNumbersDDL(clientId, taxYear);
  const invoiceNumbersDDLItems = useMemo(() => {
    if (invoiceNumbersDDL) {
      if (
        invoiceNumbersDDL &&
        Array.isArray(invoiceNumbersDDL)
      ) {
        let resources: IDropDownListResponse[] =
          sortDDLByLabel(invoiceNumbersDDL).map(
            (resourceItem: IDropDownListResponse) => ({
              // id: resourceItem.id,
              label: resourceItem.label,
              value: resourceItem.value,
            })
          );

        resources.unshift({
          label: "All",
          value: "all",
        });

        return resources;
      }
      return [];
    }
  }, [invoiceNumbersDDL]);


  const { isLoading: isLoadingTransactionDetailList, data } = useTransactionDetails(clientId, taxYear, invoiceNo); //useQuery('accountants-list', AccountantService.getAccountantList, refetchOnWindowFocus: false);
  const transactionDetailData: any = data;
  const drawerMenuOptions = useSelector(
    (state: IAppState) => state.drawerMenu.menu
  );
  const expandedLabels = ["Reports", "Accounting"];
  const expandedSalaryProfileListOrSetupItem = "Invoice";
  const enhanceData = (data: any[], parentLabel?: string): any[] => {
    return data.map((item) => {
      let isExpanded = expandedLabels.includes(item.label);
      if (
        parentLabel === "Accounting" &&
        item.label === expandedSalaryProfileListOrSetupItem
      ) {
        isExpanded = true;
      }
      const enhancedItem = {
        ...item,
        expanded: item.items ? isExpanded : false,
        items: item.items ? enhanceData(item.items, item.label) : undefined,
        path: item.label === expandedSalaryProfileListOrSetupItem && "/reports/invoice",
      };
      return enhancedItem;
    });
  };
  const onSubmit = (values: any) => {
    alert("Send Email")
  };
  const oninvoiceNumberSelectionChangeHandler = (selectedItem: any) => {
    setInvoiceNo(selectedItem.value)
    queryClient.invalidateQueries({
      queryKey: [
        "Transaction-detail",
        clientId,
        taxYear,
        selectedItem.value,
      ],
    });
  };
  useEffect(() => {
    dispatch(setSelectedActivityLogActions({
      entityId1: '',
      entityId2: '',
      entityId3: '',
      screenName: '',
      companyName: '',
      activityType: "",
      activityLogVisibility: false
    }));
    dispatch(setSelectedStickyNavBar({ isSticky: false }));
    if (drawerMenuOptions && drawerMenuOptions.length > 0) {
      const enhancedData = enhanceData(drawerMenuOptions);
      dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
    }
  }, [])

  const onSendEmailButtonClickedHandler = async () => {
    const invoiceEmail = formMethods.watch("email");
    const invoiceNumber = formMethods.watch("invoiceNumber");

    try {
      setIsLoading(true);
      const response = await ReportsService.emailInvoiceTransactionDetail(
        clientId,
        invoiceNumber,
        invoiceEmail
      );

      if (response) {
        if (response.status === 400) {
          setSnackbarData({
            open: true,
            message: response.data.message
              ? response.data.message
              : "Something went wrong.",

            severity: "error",
          });
        }
      } else {
        console.error("Error: No email received from the server");
      }
    } catch (err) {

      setIsLoading(false);
    } finally {
      setIsLoading(false);
  
    }
  };

  // getting latest updated values of form fields using watch 
  const { watch } = formMethods;
  const values = watch([
    "invoiceNumber",
  ]);

  const savePdfAsFile = (xmlData: any, filename: any) => {
    const blob = new Blob([xmlData], { type: "pdf;charset=utf-8" });
    FileSaver.saveAs(blob, filename);
  };

  const onPrintInvoiceTransactionsDetailsClickedHandler = async () => {
    const [invoiceNumber] = values;

    try {
      setIsLoading(true);
      const response = await ReportsService.exportInvoiceTransactionDetail(
        clientId,
        invoiceNumber,
      );

      if (response) {
        if (response.status === 400) {
          setSnackbarData({
            open: true,
            message: response.data.message
              ? response.data.message
              : "Something went wrong.",

            severity: "error",
          });
        } else {
          const filename = `${clientName}-invoice-transaction-details.pdf`;
          savePdfAsFile(response, filename);
        }

      } else {
        console.error("Error: No data received from the server");
      }
    } catch (err) {

      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }

  };
  return (
    <>
      <div
        style={{
          margin: "15px 0",
          fontSize: "1.5rem",
          fontWeight: "bold",
          fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
        }}
      >
        {"Invoice Transaction Details"}
      </div>
      <BackdropCircularProgress
        open={
          isLoadingTransactionDetailList || isLoading
        }
        testId="accountant Setup"
      />
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(onSubmit)}>
          {/* <Grid size={{ xs: 12, sm: 8, md: 8, lg: 8, xl: 8 }}> */}
          <Grid
            container
            size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
            sx={{
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderBottom: 0,
              borderRadius: "2px 2px 0 0",
              padding: "13px 20px 20px 20px",
              alignItems: "center"
            }}
            spacing={2}
          >
            <Grid
              size={{ xs: 12, sm: 12, md: 2, lg: 2, xl: 2 }}
              sx={{ "& .dropDownWrapper": { marginBottom: "0!important" } }}
            >
              <EnhancedDropDownRHFWithLabel
                selectName="invoiceNumber"
                label="Invoice No."
                testId={`invoiceNumber-list`}
                items={invoiceNumbersDDLItems ?? []}
                selectedItem={invoiceNo}
                onSelectionChangeHandler={oninvoiceNumberSelectionChangeHandler}
              />
            </Grid>
            <Grid size="grow" />
            <Grid
              size={{ xs: 12, sm: 12, md: 3, lg: 3, xl: 3 }}
              sx={{ "& .MuiFormControl-root": { marginBottom: "0!important", marginTop: "2px" }, mt: "1.3rem" }}>
              <EmailFieldRHF
                // label="Email"
                id="email"
                testId={`netsuite-setup-email`}
                // labelPosition={LabelPosition.Left}
                // labelColumns={4}
                // fieldColumns={8}
                // required
                placeholder="Email Address"
              />
            </Grid>
            <EnhancedButtonPrimeReact
              label="Send Email"
              raised
              // type="submit"
              severity={EnhancedButtonPrimeReactSeverity.Secondary}
              style={{ marginTop: "1.3rem" }}
              onClickHandler={onSendEmailButtonClickedHandler}
            />
            <EnhancedButtonPrimeReact
              label="Print Invoice"
              raised
              onClickHandler={onPrintInvoiceTransactionsDetailsClickedHandler}
              severity={EnhancedButtonPrimeReactSeverity.Secondary}
              style={{ marginTop: "1.3rem" }}
            />
          </Grid>
        </form>
      </FormProvider>

      {/* <Grid
                size={{ xs: 12, sm: 2, md: 1, lg: 1, xl: 1 }}
                sx={{ marginTop: "auto", marginLeft: "5px" }}
              >
              </Grid>
              <Grid size={{ xs: 0, sm: 0, md: 0.5, lg: 0.5, xl: 0.5 }}></Grid>
              <Grid
                size={{ xs: 12, sm: 3, md: 1.5, lg: 1.5, xl: 1.5 }}
                sx={{ color: "rgba(0, 0, 0, 0.6)", fontSize: "0.875rem" }}
              >
              </Grid>
              <Grid
                size={{ xs: 0, sm: 0, md: 4.95, lg: 4.95, xl: 4.95 }}
              ></Grid>
              <Grid
                size={{ xs: 12, sm: 3, md: 2, lg: 2, xl: 2 }}
                sx={{
                  textAlign: "right",
                  color: "rgba(0, 0, 0, 0.6)",
                  fontSize: "0.875rem",
                }}
              >
              </Grid> */}
      {/* </Grid> */}
      <TransactionDetailDataTable
        rows={transactionDetailData && transactionDetailData.items}
        last_rows={transactionDetailData && transactionDetailData.sumData}
        columns={transactionDetailData && transactionDetailData.headerColumns}
        showIndexColumn={true}
        showChecboxColumn={false}
        showSettingColumn={false}
        isLoading={isLoadingTransactionDetailList}
      />

      <EnhancedSnackbar
        message={snackbarData.message}
        onCloseHandler={onSnackbarClosed}
        severity={snackbarData.severity}
        testId={`snackbar`}
        open={snackbarData.open}
      />
    </>
  );
};
